import '@/pages/admin/admin.css';

import { useAuth } from '@/auth/useAuth';
import { LoadingContainer } from '@/components/ui';
import { useEmpresasSysadmin } from '@/hooks/useSysadmin';
import {
  sysadminService,
  type ArchivoDriveAuditoriaDto,
  type CarpetaDriveDto,
  type CategoriaArchivoDrive,
  type EstadoDriveEvidenciaEliminada,
  type EvidenciaEliminadaAuditoriaDto,
} from '@/services/sysadmin.service';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  File,
  FolderInput,
  PlayCircle,
  RotateCcw,
  ShieldAlert,
  Trash2,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const API_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Persistencia liviana en localStorage para que los resultados de la auditoría sobrevivan a un
 * F5 — recargar la página no debería obligar a volver a auditar todo desde cero. Nunca revienta:
 * si localStorage no está disponible (modo privado, cuota llena) simplemente no persiste.
 */
function leerLocalStorage<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function escribirLocalStorage<T>(key: string, value: T): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Silencioso a propósito: perder la persistencia no debe romper la auditoría en curso.
  }
}

const LS_KEY_CARPETA = 'driveAudit:carpeta';
const LS_KEY_PAPELERA = 'driveAudit:papelera';
const LS_KEY_GLOBAL = 'driveAudit:global';
const LS_KEY_IGNORE = 'driveAudit:ignorePatterns';

const CATEGORIA_LABEL: Record<CategoriaArchivoDrive, { label: string; className: string }> = {
  AUDIO: { label: 'Audio', className: 'bg-(--admin-blue-bg) text-(--admin-blue)' },
  EVIDENCIA: { label: 'Evidencia', className: 'bg-(--admin-purple-bg) text-(--admin-purple)' },
  ACTA: { label: 'Acta', className: 'bg-(--admin-amber-bg) text-(--admin-amber)' },
  OTRO: { label: 'Otro', className: 'bg-(--admin-surface2) text-(--admin-text3)' },
};

/**
 * Lista de nombres/patrones a ignorar (tipo .gitignore), editable desde "Todas las empresas" y
 * persistida en localStorage — comparte la misma lista en todas las vistas. Soporta coincidencia
 * exacta ("desktop.ini") o con comodín "*" ("~$*", "*.tmp"). Pensada para archivos basura que
 * generan los sistemas operativos al sincronizar carpetas (desktop.ini y Thumbs.db en Windows,
 * .DS_Store en Mac) — no son anomalías reales, solo ruido que no debería contar ni seleccionarse.
 */
function useIgnorePatterns() {
  const [patterns, setPatterns] = useState<string[]>(
    () => leerLocalStorage<string[]>(LS_KEY_IGNORE) ?? ['desktop.ini', 'Thumbs.db', '.DS_Store']
  );
  useEffect(() => {
    escribirLocalStorage(LS_KEY_IGNORE, patterns);
  }, [patterns]);
  return [patterns, setPatterns] as const;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function coincidePatronIgnorado(nombre: string, patrones: string[]): boolean {
  const nombreLower = nombre.toLowerCase();
  return patrones.some((p) => {
    const patronLower = p.trim().toLowerCase();
    if (!patronLower) return false;
    if (!patronLower.includes('*')) return nombreLower === patronLower;
    const regex = new RegExp(`^${patronLower.split('*').map(escapeRegExp).join('.*')}$`);
    return regex.test(nombreLower);
  });
}

/** Un archivo cuenta como "no reconocido" solo si es audio/evidencia/acta y no está ignorado. */
function esNoReconocidoRelevante(a: ArchivoDriveAuditoriaDto, ignorePatterns: string[]): boolean {
  return !a.reconocido && a.categoria !== 'OTRO' && !coincidePatronIgnorado(a.name, ignorePatterns);
}

/**
 * Botón de acción reversible sobre un archivo de Drive: "Eliminar" lo manda a la papelera
 * (`trashed: true`, NO borrado permanente), "Recuperar" lo saca de ahí. `trashed` refleja el
 * estado real conocido — el botón que se muestra depende de él.
 */
function BotonEliminarRecuperar({
  fileId,
  trashed,
  onChanged,
}: {
  fileId: string | null;
  trashed: boolean;
  onChanged: (nuevoTrashed: boolean) => void;
}) {
  const [enCurso, setEnCurso] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!fileId) return <span className="text-(--admin-text3)">—</span>;

  const ejecutar = async () => {
    setEnCurso(true);
    setError(null);
    try {
      const res = trashed ? await sysadminService.recuperarArchivoDrive(fileId) : await sysadminService.eliminarArchivoDrive(fileId);
      if (res.success) {
        onChanged(!trashed);
      } else {
        setError(res.message || 'Error');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setEnCurso(false);
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={ejecutar}
        disabled={enCurso}
        title={trashed ? 'Recuperar de la papelera de Drive' : 'Enviar a la papelera de Drive (reversible)'}
        className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-lg border transition-colors disabled:opacity-50 ${
          trashed
            ? 'border-(--admin-green) text-(--admin-green) hover:bg-(--admin-green-bg)'
            : 'border-(--admin-red) text-(--admin-red) hover:bg-(--admin-red-bg)'
        }`}
      >
        {trashed ? <RotateCcw className="w-3 h-3" /> : <Trash2 className="w-3 h-3" />}
        {enCurso ? '...' : trashed ? 'Recuperar' : 'Eliminar'}
      </button>
      {error && (
        <span className="text-(--admin-red) text-[10px]" title={error}>
          Error
        </span>
      )}
    </div>
  );
}

// ─── Evidencias eliminadas vs estado real en Drive ─────────────────────────

const ESTADO_LABEL: Record<EstadoDriveEvidenciaEliminada, { label: string; className: string }> = {
  TRASHED_OK: { label: 'En papelera (OK)', className: 'bg-(--admin-green-bg) text-(--admin-green)' },
  NO_EXISTE: { label: 'Purgado de Drive (OK)', className: 'bg-(--admin-green-bg) text-(--admin-green)' },
  SIGUE_VIVA: { label: 'Anomalía: sigue viva', className: 'bg-(--admin-red-bg) text-(--admin-red)' },
  URL_INVALIDA: { label: 'URL no interpretable', className: 'bg-(--admin-amber-bg) text-(--admin-amber)' },
  ERROR: { label: 'Error al consultar Drive', className: 'bg-(--admin-amber-bg) text-(--admin-amber)' },
};

function EvidenciasEliminadasTab() {
  const [resultados, setResultados] = useState<EvidenciaEliminadaAuditoriaDto[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [soloAnomalias, setSoloAnomalias] = useState(false);

  const ejecutar = () => {
    setIsLoading(true);
    setError(null);
    sysadminService
      .auditarEvidenciasEliminadas()
      .then((res) => {
        if (res.success) setResultados(res.data);
        else setError('Error al ejecutar la auditoría');
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Error al ejecutar la auditoría'))
      .finally(() => setIsLoading(false));
  };

  const anomalias = resultados?.filter((r) => r.driveStatus === 'SIGUE_VIVA') ?? [];
  const visibles = soloAnomalias ? anomalias : (resultados ?? []);

  return (
    <div className="space-y-4">
      <div className="admin-card p-4 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[12px] text-(--admin-text3) max-w-2xl">
          Consulta en vivo, contra la API de Drive, cada evidencia marcada como eliminada en el sistema. Que un archivo ya no exista en
          Drive (purgado de la papelera) es el desenlace normal — la anomalía real es que el archivo <strong>siga vivo</strong> pese a que
          la aplicación cree que se eliminó. No borra ni modifica nada.
        </p>
        <button
          type="button"
          onClick={ejecutar}
          disabled={isLoading}
          className="admin-btn text-[12px] py-1.5 px-3 shrink-0 disabled:opacity-50"
        >
          {isLoading ? 'Auditando...' : 'Ejecutar auditoría'}
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-(--admin-red) bg-(--admin-red-bg) px-4 py-3 text-[12px] text-(--admin-red)">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {isLoading && <LoadingContainer text="Consultando Google Drive, puede tardar unos segundos..." />}

      {resultados && !isLoading && (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="admin-chip text-[11px] bg-(--admin-red-bg) text-(--admin-red)">{anomalias.length} anomalías</span>
            <span className="admin-chip text-[11px] bg-(--admin-surface2) text-(--admin-text3)">
              {resultados.length} evidencias revisadas
            </span>
            <label className="flex items-center gap-1.5 text-[11px] text-(--admin-text3) cursor-pointer ml-auto">
              <input type="checkbox" checked={soloAnomalias} onChange={(e) => setSoloAnomalias(e.target.checked)} />
              Mostrar solo anomalías
            </label>
          </div>

          {visibles.length === 0 ? (
            <div className="admin-card p-10 text-center text-(--admin-text3)">
              <ShieldAlert className="w-8 h-8 mx-auto mb-2 opacity-20" />
              <div className="text-[13px]">
                {soloAnomalias ? 'No hay anomalías: todo lo eliminado se eliminó de verdad.' : 'Sin resultados.'}
              </div>
            </div>
          ) : (
            <div className="admin-card overflow-x-auto">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Evidencia</th>
                    <th>Empresa</th>
                    <th>Asesoría</th>
                    <th>Estado en Drive</th>
                    <th>GoogleJob de borrado</th>
                    <th>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {visibles.map((r) => {
                    const estado = ESTADO_LABEL[r.driveStatus];
                    return (
                      <tr key={r.evidenciaId}>
                        <td className="text-(--admin-text) max-w-70 truncate whitespace-nowrap" title={`#${r.evidenciaId} ${r.nombre}`}>
                          #{r.evidenciaId} {r.nombre}
                        </td>
                        <td className="text-(--admin-text2) max-w-40 truncate whitespace-nowrap" title={r.empresaNombre}>
                          {r.empresaNombre}
                        </td>
                        <td className="text-(--admin-text2) font-mono" style={{ textAlign: 'right' }}>
                          {r.asesoriaId}
                        </td>
                        <td>
                          <span className={`admin-chip text-[10px] whitespace-nowrap ${estado.className}`} title={r.detalle}>
                            {estado.label}
                          </span>
                        </td>
                        <td
                          className="text-(--admin-text3) text-[11px] max-w-60 truncate whitespace-nowrap"
                          title={r.jobError ?? undefined}
                        >
                          {r.jobStatus}
                          {r.jobIntentos > 0 && ` (${r.jobIntentos} intentos)`}
                          {r.jobError && ` — ${r.jobError}`}
                        </td>
                        <td>
                          {(r.driveStatus === 'SIGUE_VIVA' || r.driveStatus === 'TRASHED_OK') && (
                            <BotonEliminarRecuperar
                              fileId={r.fileId}
                              trashed={r.driveStatus === 'TRASHED_OK'}
                              onChanged={(nuevoTrashed) =>
                                setResultados((prev) =>
                                  (prev ?? []).map((x) =>
                                    x.evidenciaId === r.evidenciaId ? { ...x, driveStatus: nuevoTrashed ? 'TRASHED_OK' : 'SIGUE_VIVA' } : x
                                  )
                                )
                              }
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Panel para mover uno o más archivos a una carpeta existente de la empresa, o a una nueva que
 * se crea al vuelo dentro de la carpeta que se elija como padre.
 */
function PanelMoverArchivos({
  nit,
  fileIds,
  onClose,
  onMovido,
}: {
  nit: string;
  fileIds: string[];
  onClose: () => void;
  onMovido: () => void;
}) {
  const [carpetas, setCarpetas] = useState<CarpetaDriveDto[] | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modo, setModo] = useState<'existente' | 'nueva'>('existente');
  const [carpetaDestinoId, setCarpetaDestinoId] = useState('');
  const [carpetaPadreId, setCarpetaPadreId] = useState('');
  const [nuevaCarpetaNombre, setNuevaCarpetaNombre] = useState('');
  const [moviendo, setMoviendo] = useState(false);

  useEffect(() => {
    sysadminService
      .listarCarpetasEmpresaDrive(nit)
      .then((res) => {
        if (res.success && res.carpetas) {
          setCarpetas(res.carpetas);
          setCarpetaDestinoId(res.carpetas[0]?.id ?? '');
          setCarpetaPadreId(res.carpetas[0]?.id ?? '');
        } else {
          setError(res.message || 'Error al listar las carpetas de la empresa');
        }
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Error al listar las carpetas de la empresa'))
      .finally(() => setCargando(false));
  }, [nit]);

  const confirmar = async () => {
    setMoviendo(true);
    setError(null);
    try {
      const destino = modo === 'existente' ? { targetFolderId: carpetaDestinoId } : { parentFolderId: carpetaPadreId, nuevaCarpetaNombre };
      const res = await sysadminService.moverArchivosDrive({ fileIds, ...destino });
      if (res.success) {
        onMovido();
      } else {
        setError(res.message || 'Error al mover');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al mover');
    } finally {
      setMoviendo(false);
    }
  };

  return (
    <div className="p-3 bg-(--admin-surface2) border-b border-(--admin-border) space-y-2">
      <div className="flex items-center gap-2 text-[12px]">
        <span className="font-semibold text-(--admin-text)">
          Mover {fileIds.length} archivo{fileIds.length !== 1 && 's'} a...
        </span>
        <button type="button" onClick={onClose} className="ml-auto text-[11px] text-(--admin-text3) hover:text-(--admin-text) underline">
          Cancelar
        </button>
      </div>

      {cargando ? (
        <div className="text-[12px] text-(--admin-text3)">Cargando carpetas de la empresa...</div>
      ) : (
        <>
          <div className="flex items-center gap-3 text-[12px] text-(--admin-text)">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" checked={modo === 'existente'} onChange={() => setModo('existente')} />
              Carpeta existente
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" checked={modo === 'nueva'} onChange={() => setModo('nueva')} />
              Carpeta nueva
            </label>
          </div>

          {modo === 'existente' ? (
            <select
              value={carpetaDestinoId}
              onChange={(e) => setCarpetaDestinoId(e.target.value)}
              className="w-full max-w-md text-[12px] py-1.5 px-2.5 border border-(--admin-border2) rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-(--admin-surface) text-(--admin-text)"
            >
              {carpetas?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.path}
                </option>
              ))}
            </select>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={carpetaPadreId}
                onChange={(e) => setCarpetaPadreId(e.target.value)}
                className="text-[12px] py-1.5 px-2.5 border border-(--admin-border2) rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-(--admin-surface) text-(--admin-text)"
              >
                {carpetas?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.path}
                  </option>
                ))}
              </select>
              <span className="text-[11px] text-(--admin-text3)">/</span>
              <input
                type="text"
                value={nuevaCarpetaNombre}
                onChange={(e) => setNuevaCarpetaNombre(e.target.value)}
                placeholder="Nombre de la carpeta nueva"
                className="text-[12px] py-1.5 px-2.5 border border-(--admin-border2) rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-(--admin-surface) text-(--admin-text)"
              />
            </div>
          )}

          {error && <div className="text-[11px] text-(--admin-red)">{error}</div>}

          <button
            type="button"
            onClick={confirmar}
            disabled={moviendo || (modo === 'nueva' && !nuevaCarpetaNombre.trim()) || (modo === 'existente' && !carpetaDestinoId)}
            className="admin-btn text-[12px] py-1.5 px-3 disabled:opacity-50"
          >
            {moviendo ? 'Moviendo...' : 'Confirmar'}
          </button>
        </>
      )}
    </div>
  );
}

/**
 * Tabla de archivos de Drive con estado (reconocido/no reconocido/eliminado localmente), su
 * categoría (Audio/Evidencia/Acta/Otro), selección con checkbox y botones de eliminar/recuperar
 * y mover. Reutilizada por "Carpeta vs. BD activa" (una empresa) y la auditoría global (todas las
 * empresas, agrupadas).
 */
function TablaArchivosDrive({
  nit,
  archivos,
  eliminadosLocalmente,
  onToggleEliminado,
  onMovidos,
}: {
  nit: string;
  archivos: ArchivoDriveAuditoriaDto[];
  eliminadosLocalmente: Set<string>;
  onToggleEliminado: (fileId: string, nuevoTrashed: boolean) => void;
  onMovidos: (fileIds: string[]) => void;
}) {
  const [seleccionados, setSeleccionados] = useState<Set<string>>(new Set());
  const [mostrarMover, setMostrarMover] = useState(false);

  // Si la lista de archivos cambia (nueva auditoría, filtro, etc.) se descarta cualquier
  // selección que ya no esté presente, para no arrastrar ids "fantasma".
  useEffect(() => {
    setSeleccionados((prev) => {
      const idsVisibles = new Set(archivos.map((a) => a.id));
      const next = new Set(Array.from(prev).filter((id) => idsVisibles.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [archivos]);

  const todosSeleccionados = archivos.length > 0 && archivos.every((a) => seleccionados.has(a.id));

  return (
    <div>
      {seleccionados.size > 0 && (
        <div className="p-2 flex items-center gap-2 bg-(--admin-surface2) border-b border-(--admin-border)">
          <span className="text-[11px] text-(--admin-text3)">{seleccionados.size} seleccionados</span>
          <button
            type="button"
            onClick={() => setMostrarMover(true)}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-lg border border-(--admin-border2) text-(--admin-text) hover:bg-(--admin-surface) transition-colors"
          >
            <FolderInput className="w-3 h-3" />
            Mover...
          </button>
          <button
            type="button"
            onClick={() => setSeleccionados(new Set())}
            className="text-[11px] text-(--admin-text3) hover:text-(--admin-text) underline"
          >
            Limpiar selección
          </button>
        </div>
      )}
      {mostrarMover && (
        <PanelMoverArchivos
          nit={nit}
          fileIds={Array.from(seleccionados)}
          onClose={() => setMostrarMover(false)}
          onMovido={() => {
            onMovidos(Array.from(seleccionados));
            setSeleccionados(new Set());
            setMostrarMover(false);
          }}
        />
      )}
      <table className="admin-table">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                checked={todosSeleccionados}
                onChange={(e) => setSeleccionados(e.target.checked ? new Set(archivos.map((a) => a.id)) : new Set())}
              />
            </th>
            <th>Archivo</th>
            <th>Categoría</th>
            <th>Ruta</th>
            <th>Tipo</th>
            <th>Estado</th>
            <th>Acción</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {archivos.map((a) => {
            const eliminadoAhora = eliminadosLocalmente.has(a.id);
            const categoria = CATEGORIA_LABEL[a.categoria];
            return (
              <tr key={a.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={seleccionados.has(a.id)}
                    onChange={(e) =>
                      setSeleccionados((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(a.id);
                        else next.delete(a.id);
                        return next;
                      })
                    }
                  />
                </td>
                <td className="text-(--admin-text)">
                  <div className="flex items-center gap-1.5 max-w-60">
                    {a.mimeType.startsWith('audio/') ? (
                      <PlayCircle className="w-3.5 h-3.5 text-(--admin-text3) shrink-0" />
                    ) : (
                      <File className="w-3.5 h-3.5 text-(--admin-text3) shrink-0" />
                    )}
                    <span className="truncate whitespace-nowrap" title={a.name}>
                      {a.name}
                    </span>
                  </div>
                </td>
                <td>
                  <span className={`admin-chip text-[10px] whitespace-nowrap ${categoria.className}`}>{categoria.label}</span>
                </td>
                <td className="text-(--admin-text3) text-[11px] font-mono max-w-70 truncate whitespace-nowrap" title={a.path}>
                  {a.path}
                </td>
                <td className="text-(--admin-text3) text-[11px] max-w-40 truncate whitespace-nowrap" title={a.mimeType}>
                  {a.mimeType}
                </td>
                <td>
                  {eliminadoAhora ? (
                    <span className="admin-chip text-[10px] whitespace-nowrap bg-(--admin-amber-bg) text-(--admin-amber)">
                      Eliminado (recuperable)
                    </span>
                  ) : a.reconocido ? (
                    <span className="admin-chip text-[10px] whitespace-nowrap bg-(--admin-green-bg) text-(--admin-green)">Reconocido</span>
                  ) : (
                    <span className="admin-chip text-[10px] whitespace-nowrap bg-(--admin-red-bg) text-(--admin-red)">No reconocido</span>
                  )}
                </td>
                <td>
                  {!a.reconocido && (
                    <BotonEliminarRecuperar
                      fileId={a.id}
                      trashed={eliminadoAhora}
                      onChanged={(nuevoTrashed) => onToggleEliminado(a.id, nuevoTrashed)}
                    />
                  )}
                </td>
                <td>
                  {a.webViewLink && (
                    <a href={a.webViewLink} target="_blank" rel="noopener noreferrer" className="text-(--admin-text3) hover:text-blue-600">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Carpeta de Drive de una empresa vs BD activa ──────────────────────────

interface CarpetaPersistida {
  nit: string;
  empresaNombre: string;
  archivos: ArchivoDriveAuditoriaDto[];
  eliminadosLocalmente: string[];
}

function CarpetaEmpresaTab() {
  const { empresas, isLoading: cargandoEmpresas } = useEmpresasSysadmin();
  const persistido = leerLocalStorage<CarpetaPersistida>(LS_KEY_CARPETA);
  const [nit, setNit] = useState(persistido?.nit ?? '');
  const [archivos, setArchivos] = useState<ArchivoDriveAuditoriaDto[] | null>(persistido?.archivos ?? null);
  const [empresaNombre, setEmpresaNombre] = useState(persistido?.empresaNombre ?? '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [soloNoReconocidos, setSoloNoReconocidos] = useState(true);
  // Archivos que el propio admin eliminó (mandó a la papelera) desde esta vista — se siguen
  // mostrando (con opción de "Recuperar") en vez de desaparecer, para que el borrado sea visible
  // y reversible sin tener que volver a correr la auditoría. Persiste en localStorage junto con
  // los archivos: recargar la página no debería perder ni la lista ni lo que se eliminó.
  const [eliminadosLocalmente, setEliminadosLocalmente] = useState<Set<string>>(new Set(persistido?.eliminadosLocalmente ?? []));
  const [ignorePatterns] = useIgnorePatterns();

  useEffect(() => {
    if (!nit || !archivos) return;
    escribirLocalStorage<CarpetaPersistida>(LS_KEY_CARPETA, {
      nit,
      empresaNombre,
      archivos,
      eliminadosLocalmente: Array.from(eliminadosLocalmente),
    });
  }, [nit, empresaNombre, archivos, eliminadosLocalmente]);

  const ejecutar = () => {
    if (!nit) return;
    setIsLoading(true);
    setError(null);
    setArchivos(null);
    setEliminadosLocalmente(new Set());
    sysadminService
      .auditarCarpetaEmpresa(nit)
      .then((res) => {
        if (res.success && res.archivos) {
          setArchivos(res.archivos);
          setEmpresaNombre(res.empresaNombre ?? '');
        } else {
          setError(res.message || 'Error al auditar la carpeta');
        }
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Error al auditar la carpeta'))
      .finally(() => setIsLoading(false));
  };

  const noReconocidos = (archivos ?? []).filter((a) => esNoReconocidoRelevante(a, ignorePatterns));
  const visibles = soloNoReconocidos ? noReconocidos : (archivos ?? []);

  return (
    <div className="space-y-4">
      <div className="admin-card p-4 space-y-3">
        <p className="text-[12px] text-(--admin-text3) max-w-2xl">
          Recorre recursivamente la carpeta de Drive de una empresa (evidencias, audios y actas, con subcarpetas) y la compara contra lo que
          hoy está activo en la base de datos. Un archivo "no reconocido" puede ser un huérfano de un borrado que falló, o un archivo subido
          manualmente a Drive sin pasar por la aplicación. "Eliminar" lo manda a la papelera de Drive (reversible) — nunca lo borra de forma
          permanente — y se puede "Recuperar" mientras siga ahí.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={nit}
            onChange={(e) => setNit(e.target.value)}
            disabled={cargandoEmpresas}
            className="text-[12px] py-1.5 px-2.5 max-w-xs border border-(--admin-border2) rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-(--admin-surface) text-(--admin-text)"
          >
            <option value="">Selecciona una empresa...</option>
            {empresas.map((e) => (
              <option key={e.nitId} value={e.nitId}>
                {e.nombre}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={ejecutar}
            disabled={!nit || isLoading}
            className="admin-btn text-[12px] py-1.5 px-3 disabled:opacity-50"
          >
            {isLoading ? 'Auditando...' : 'Auditar carpeta'}
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-(--admin-red) bg-(--admin-red-bg) px-4 py-3 text-[12px] text-(--admin-red)">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {isLoading && <LoadingContainer text="Recorriendo la carpeta de Drive, puede tardar según la cantidad de archivos..." />}

      {archivos && !isLoading && (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[13px] font-semibold text-(--admin-text)">{empresaNombre}</span>
            <span className="admin-chip text-[11px] bg-(--admin-red-bg) text-(--admin-red)">{noReconocidos.length} no reconocidos</span>
            <span className="admin-chip text-[11px] bg-(--admin-surface2) text-(--admin-text3)">{archivos.length} archivos totales</span>
            <label className="flex items-center gap-1.5 text-[11px] text-(--admin-text3) cursor-pointer ml-auto">
              <input type="checkbox" checked={soloNoReconocidos} onChange={(e) => setSoloNoReconocidos(e.target.checked)} />
              Mostrar solo no reconocidos
            </label>
          </div>

          {visibles.length === 0 ? (
            <div className="admin-card p-10 text-center text-(--admin-text3)">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2 opacity-20" />
              <div className="text-[13px]">
                {soloNoReconocidos ? 'Todo lo que hay en la carpeta de Drive coincide con un registro activo.' : 'La carpeta está vacía.'}
              </div>
            </div>
          ) : (
            <div className="admin-card overflow-x-auto">
              <TablaArchivosDrive
                nit={nit}
                archivos={visibles}
                eliminadosLocalmente={eliminadosLocalmente}
                onToggleEliminado={(fileId, nuevoTrashed) =>
                  setEliminadosLocalmente((prev) => {
                    const next = new Set(prev);
                    if (nuevoTrashed) next.add(fileId);
                    else next.delete(fileId);
                    return next;
                  })
                }
                onMovidos={(fileIds) => setArchivos((prev) => prev?.filter((a) => !fileIds.includes(a.id)) ?? null)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Auditoría global: todas las empresas por SSE, agrupadas por empresa ───

interface ResultadoEmpresaStream {
  ok: boolean;
  empresaId: string;
  empresaNombre: string;
  totalArchivos?: number;
  totalNoReconocidos?: number;
  archivos?: ArchivoDriveAuditoriaDto[];
  error?: string;
  completadas: number;
  total: number;
}

function GrupoEmpresaResultado({
  resultado,
  ignorePatterns,
  onArchivosMovidos,
}: {
  resultado: ResultadoEmpresaStream;
  ignorePatterns: string[];
  onArchivosMovidos: (empresaId: string, fileIds: string[]) => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const [eliminadosLocalmente, setEliminadosLocalmente] = useState<Set<string>>(new Set());

  if (!resultado.ok) {
    return (
      <div className="admin-card p-3 flex items-center gap-2 text-[12px] text-(--admin-red)">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <strong>{resultado.empresaNombre}</strong> — error al auditar: {resultado.error}
      </div>
    );
  }

  const archivos = resultado.archivos ?? [];
  const noReconocidos = archivos.filter((a) => esNoReconocidoRelevante(a, ignorePatterns));

  return (
    <div className="admin-card overflow-hidden">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="w-full flex items-center gap-2 p-3 text-left hover:bg-(--admin-surface2) transition-colors"
      >
        {abierto ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
        <span className="text-[13px] font-semibold text-(--admin-text)">{resultado.empresaNombre}</span>
        <span className="admin-chip text-[10px] whitespace-nowrap bg-(--admin-red-bg) text-(--admin-red)">
          {noReconocidos.length} no reconocidos
        </span>
        <span className="admin-chip text-[10px] whitespace-nowrap bg-(--admin-surface2) text-(--admin-text3)">
          {resultado.totalArchivos} archivos totales
        </span>
      </button>
      {abierto && noReconocidos.length > 0 && (
        <div className="overflow-x-auto border-t border-(--admin-border)">
          <TablaArchivosDrive
            nit={resultado.empresaId}
            archivos={noReconocidos}
            eliminadosLocalmente={eliminadosLocalmente}
            onToggleEliminado={(fileId, nuevoTrashed) =>
              setEliminadosLocalmente((prev) => {
                const next = new Set(prev);
                if (nuevoTrashed) next.add(fileId);
                else next.delete(fileId);
                return next;
              })
            }
            onMovidos={(fileIds) => onArchivosMovidos(resultado.empresaId, fileIds)}
          />
        </div>
      )}
    </div>
  );
}

interface GlobalPersistido {
  resultados: ResultadoEmpresaStream[];
  terminado: boolean;
}

function AuditoriaGlobalTab() {
  const { token: rawToken } = useAuth();
  const { empresas } = useEmpresasSysadmin();
  const persistido = leerLocalStorage<GlobalPersistido>(LS_KEY_GLOBAL);
  const [resultados, setResultados] = useState<ResultadoEmpresaStream[]>(persistido?.resultados ?? []);
  const [enProgreso, setEnProgreso] = useState(false);
  const [terminado, setTerminado] = useState(persistido?.terminado ?? false);
  const [error, setError] = useState<string | null>(null);
  const [filtroEstado, setFiltroEstado] = useState<'con-problemas' | 'sin-problemas' | 'con-error' | 'todas'>('con-problemas');
  const [busqueda, setBusqueda] = useState('');
  const eventSourceRef = useRef<EventSource | null>(null);

  // Lista de nombres/patrones a ignorar (tipo .gitignore) — desktop.ini, Thumbs.db, .DS_Store,
  // etc., archivos basura de sincronización que no son anomalías reales. Compartida por todas las
  // vistas vía localStorage, editable acá.
  const [ignorePatterns, setIgnorePatterns] = useIgnorePatterns();
  const [mostrarIgnorados, setMostrarIgnorados] = useState(false);
  const [nuevoPatron, setNuevoPatron] = useState('');

  // Selección manual de empresas puntuales para auditar, en vez de correr el barrido completo.
  const [mostrarSelector, setMostrarSelector] = useState(false);
  const [seleccionadas, setSeleccionadas] = useState<Set<string>>(new Set());
  const [busquedaSelector, setBusquedaSelector] = useState('');
  const empresasFiltradasSelector = busquedaSelector
    ? empresas.filter((e) => e.nombre.toLowerCase().includes(busquedaSelector.toLowerCase()))
    : empresas;

  const onArchivosMovidos = (empresaId: string, fileIds: string[]) => {
    setResultados((prev) =>
      prev.map((r) =>
        r.empresaId === empresaId && r.ok ? { ...r, archivos: (r.archivos ?? []).filter((a) => !fileIds.includes(a.id)) } : r
      )
    );
  };

  const cleanToken = (typeof rawToken === 'string' ? rawToken : '').replace(/^Bearer\s+/i, '').trim();
  // Si al recargar la página quedaron resultados sin terminar el barrido, es una auditoría
  // interrumpida a mitad de camino (ej. cerró la pestaña) — el botón pasa a "Reanudar" en vez de
  // arrancar todo de cero, y el SSE recibe qué empresas saltarse. No aplica si hay una selección
  // manual activa — eso siempre es una corrida nueva y acotada.
  const interrumpida = seleccionadas.size === 0 && !terminado && resultados.length > 0;

  useEffect(() => {
    escribirLocalStorage<GlobalPersistido>(LS_KEY_GLOBAL, { resultados, terminado });
  }, [resultados, terminado]);

  const iniciar = () => {
    if (!cleanToken) {
      setError('Token de autenticación inválido o ausente');
      return;
    }
    if (eventSourceRef.current) eventSourceRef.current.close();

    const incluirIds = Array.from(seleccionadas);
    const reanudando = incluirIds.length === 0 && interrumpida;
    const excluirIds = reanudando ? resultados.map((r) => r.empresaId) : [];

    if (!reanudando) setResultados([]);
    setError(null);
    setTerminado(false);
    setEnProgreso(true);

    const url = new URL(`${API_URL}/api/sysadmin/drive-audit/auditoria-global/stream`, window.location.origin);
    url.searchParams.append('token', cleanToken);
    if (excluirIds.length > 0) url.searchParams.append('excluir', excluirIds.join(','));
    if (incluirIds.length > 0) url.searchParams.append('incluir', incluirIds.join(','));

    const source = new EventSource(url.toString());
    eventSourceRef.current = source;

    source.addEventListener('empresa', (event: MessageEvent) => {
      try {
        const data: ResultadoEmpresaStream = JSON.parse(event.data);
        setResultados((prev) => [...prev, data]);
      } catch (e) {
        console.error('Error parseando resultado de empresa', e);
      }
    });

    source.addEventListener('done', () => {
      setEnProgreso(false);
      setTerminado(true);
      source.close();
    });

    source.onerror = () => {
      setEnProgreso(false);
      setError(
        'Se perdió la conexión con el servidor a mitad de la auditoría — los resultados ya recibidos siguen abajo, se puede reanudar.'
      );
      source.close();
    };
  };

  useEffect(() => {
    return () => {
      eventSourceRef.current?.close();
    };
  }, []);

  const total = resultados[0]?.total ?? 0;
  const completadas = resultados.length;
  // Cuenta en el cliente (no el totalNoReconocidos del backend) para que los archivos ignorados
  // no inflen "con problemas" — el admin definió esos patrones como ruido, no como anomalías.
  const contarNoReconocidosEfectivo = (r: ResultadoEmpresaStream) =>
    r.ok ? (r.archivos ?? []).filter((a) => esNoReconocidoRelevante(a, ignorePatterns)).length : 0;
  const conProblemas = resultados.filter((r) => r.ok && contarNoReconocidosEfectivo(r) > 0);
  const sinProblemas = resultados.filter((r) => r.ok && contarNoReconocidosEfectivo(r) === 0);
  const conError = resultados.filter((r) => !r.ok);

  const porEstado =
    filtroEstado === 'con-problemas'
      ? conProblemas
      : filtroEstado === 'sin-problemas'
        ? sinProblemas
        : filtroEstado === 'con-error'
          ? conError
          : resultados;
  const busquedaNormalizada = busqueda.trim().toLowerCase();
  const visibles = busquedaNormalizada ? porEstado.filter((r) => r.empresaNombre.toLowerCase().includes(busquedaNormalizada)) : porEstado;

  return (
    <div className="space-y-4">
      <div className="admin-card p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[12px] text-(--admin-text3) max-w-2xl">
            Corre "Carpeta vs. BD activa" para TODAS las empresas a la vez (streaming por SSE, 4 a la vez) — cada empresa aparece apenas
            termina su auditoría, agrupada en su propia sección, en vez de esperar a que termine todo el barrido para ver algo. Si se
            recarga la página a mitad de camino, los resultados quedan guardados y se puede reanudar sin repetir lo ya hecho.
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setMostrarIgnorados((v) => !v)}
              className="text-[11px] text-(--admin-text3) hover:text-(--admin-text) underline"
            >
              {mostrarIgnorados ? 'Ocultar ignorados' : `Ignorados (${ignorePatterns.length})`}
            </button>
            <button
              type="button"
              onClick={() => setMostrarSelector((v) => !v)}
              className="text-[11px] text-(--admin-text3) hover:text-(--admin-text) underline"
            >
              {mostrarSelector ? 'Ocultar selector' : 'Elegir empresas específicas'}
            </button>
            {interrumpida && !enProgreso && (
              <button
                type="button"
                onClick={() => {
                  setResultados([]);
                  setTerminado(false);
                }}
                className="text-[11px] text-(--admin-text3) hover:text-(--admin-text) underline"
                title="Descarta el progreso guardado y vuelve a empezar desde cero"
              >
                Empezar de cero
              </button>
            )}
            <button type="button" onClick={iniciar} disabled={enProgreso} className="admin-btn text-[12px] py-1.5 px-3 disabled:opacity-50">
              {enProgreso
                ? 'Auditando...'
                : seleccionadas.size > 0
                  ? `Auditar ${seleccionadas.size} seleccionadas`
                  : interrumpida
                    ? 'Reanudar auditoría'
                    : 'Auditar TODAS las empresas'}
            </button>
          </div>
        </div>

        {mostrarIgnorados && (
          <div className="border-t border-(--admin-border) pt-3 space-y-2">
            <p className="text-[11px] text-(--admin-text3) max-w-2xl">
              Archivos que se ignoran en todas las vistas (no cuentan como "no reconocidos" ni se pueden seleccionar) — coincidencia exacta
              o con comodín "*", ej. <code>desktop.ini</code> o <code>~$*</code>.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="text"
                value={nuevoPatron}
                onChange={(e) => setNuevoPatron(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' || !nuevoPatron.trim()) return;
                  setIgnorePatterns((prev) => (prev.includes(nuevoPatron.trim()) ? prev : [...prev, nuevoPatron.trim()]));
                  setNuevoPatron('');
                }}
                placeholder="Nuevo patrón (ej. desktop.ini)..."
                className="text-[12px] py-1.5 px-2.5 max-w-xs border border-(--admin-border2) rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-(--admin-surface) text-(--admin-text)"
              />
              <button
                type="button"
                onClick={() => {
                  if (!nuevoPatron.trim()) return;
                  setIgnorePatterns((prev) => (prev.includes(nuevoPatron.trim()) ? prev : [...prev, nuevoPatron.trim()]));
                  setNuevoPatron('');
                }}
                className="admin-btn text-[12px] py-1.5 px-3"
              >
                Agregar
              </button>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {ignorePatterns.length === 0 && <span className="text-[11px] text-(--admin-text3)">Sin patrones ignorados todavía.</span>}
              {ignorePatterns.map((p) => (
                <span
                  key={p}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-mono rounded-lg bg-(--admin-surface2) text-(--admin-text)"
                >
                  {p}
                  <button
                    type="button"
                    onClick={() => setIgnorePatterns((prev) => prev.filter((x) => x !== p))}
                    className="text-(--admin-text3) hover:text-(--admin-red)"
                    title="Quitar de la lista de ignorados"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {mostrarSelector && (
          <div className="border-t border-(--admin-border) pt-3 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="text"
                value={busquedaSelector}
                onChange={(e) => setBusquedaSelector(e.target.value)}
                placeholder="Buscar empresa para seleccionar..."
                className="text-[12px] py-1.5 px-2.5 max-w-xs border border-(--admin-border2) rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-(--admin-surface) text-(--admin-text)"
              />
              <button
                type="button"
                onClick={() => setSeleccionadas(new Set(empresasFiltradasSelector.map((e) => e.nitId)))}
                className="text-[11px] text-(--admin-text3) hover:text-(--admin-text) underline"
              >
                Seleccionar visibles ({empresasFiltradasSelector.length})
              </button>
              {seleccionadas.size > 0 && (
                <button
                  type="button"
                  onClick={() => setSeleccionadas(new Set())}
                  className="text-[11px] text-(--admin-text3) hover:text-(--admin-text) underline"
                >
                  Limpiar selección
                </button>
              )}
              <span className="text-[11px] text-(--admin-text3) ml-auto">{seleccionadas.size} seleccionadas</span>
            </div>
            <div className="max-h-56 overflow-y-auto border border-(--admin-border) rounded-lg divide-y divide-(--admin-border)">
              {empresasFiltradasSelector.map((e) => (
                <label
                  key={e.nitId}
                  className="flex items-center gap-2 px-3 py-1.5 text-[12px] text-(--admin-text) hover:bg-(--admin-surface2) cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={seleccionadas.has(e.nitId)}
                    onChange={(ev) =>
                      setSeleccionadas((prev) => {
                        const next = new Set(prev);
                        if (ev.target.checked) next.add(e.nitId);
                        else next.delete(e.nitId);
                        return next;
                      })
                    }
                  />
                  {e.nombre}
                </label>
              ))}
              {empresasFiltradasSelector.length === 0 && (
                <div className="px-3 py-4 text-[12px] text-(--admin-text3) text-center">Ninguna empresa coincide con la búsqueda.</div>
              )}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-(--admin-red) bg-(--admin-red-bg) px-4 py-3 text-[12px] text-(--admin-red)">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {(enProgreso || resultados.length > 0) && (
        <div className="admin-card p-3 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="admin-chip text-[11px] bg-(--admin-surface2) text-(--admin-text3)">
              {completadas}/{total || '…'} empresas revisadas{enProgreso && '...'}
            </span>
            {terminado && <span className="admin-chip text-[11px] bg-(--admin-green-bg) text-(--admin-green)">Auditoría completa</span>}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar empresa..."
              className="text-[12px] py-1.5 px-2.5 max-w-xs border border-(--admin-border2) rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-(--admin-surface) text-(--admin-text)"
            />
            <div className="flex items-center gap-1">
              {(
                [
                  {
                    key: 'con-problemas',
                    label: 'Con no reconocidos',
                    count: conProblemas.length,
                    className: 'bg-(--admin-red-bg) text-(--admin-red)',
                  },
                  {
                    key: 'sin-problemas',
                    label: 'Sin problemas',
                    count: sinProblemas.length,
                    className: 'bg-(--admin-green-bg) text-(--admin-green)',
                  },
                  { key: 'con-error', label: 'Con error', count: conError.length, className: 'bg-(--admin-amber-bg) text-(--admin-amber)' },
                  { key: 'todas', label: 'Todas', count: resultados.length, className: 'bg-(--admin-surface2) text-(--admin-text3)' },
                ] as const
              ).map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFiltroEstado(f.key)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-lg border transition-colors ${
                    filtroEstado === f.key
                      ? `${f.className} border-transparent`
                      : 'border-(--admin-border2) text-(--admin-text3) hover:bg-(--admin-surface2)'
                  }`}
                >
                  {f.label}
                  <span className="opacity-70">({f.count})</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {terminado && visibles.length === 0 && (
        <div className="admin-card p-10 text-center text-(--admin-text3)">
          <CheckCircle2 className="w-8 h-8 mx-auto mb-2 opacity-20" />
          <div className="text-[13px]">
            {busquedaNormalizada ? 'Ninguna empresa coincide con la búsqueda.' : 'Ninguna empresa coincide con este filtro.'}
          </div>
        </div>
      )}

      <div className="space-y-2">
        {visibles.map((r) => (
          <GrupoEmpresaResultado key={r.empresaId} resultado={r} ignorePatterns={ignorePatterns} onArchivosMovidos={onArchivosMovidos} />
        ))}
      </div>
    </div>
  );
}

// ─── Papelera de Drive de una empresa (lo que hoy está trasheado) ──────────

interface PapeleraPersistida {
  nit: string;
  empresaNombre: string;
  archivos: ArchivoDriveAuditoriaDto[];
  recuperados: string[];
}

function PapeleraEmpresaTab() {
  const { empresas, isLoading: cargandoEmpresas } = useEmpresasSysadmin();
  const persistido = leerLocalStorage<PapeleraPersistida>(LS_KEY_PAPELERA);
  const [nit, setNit] = useState(persistido?.nit ?? '');
  const [archivos, setArchivos] = useState<ArchivoDriveAuditoriaDto[] | null>(persistido?.archivos ?? null);
  const [empresaNombre, setEmpresaNombre] = useState(persistido?.empresaNombre ?? '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Ids recuperados — se muestran con un chip "Recuperado" en vez de desaparecer. Persiste junto
  // con la lista de archivos para que recargar la página no la deje en blanco.
  const [recuperados, setRecuperados] = useState<Set<string>>(new Set(persistido?.recuperados ?? []));

  useEffect(() => {
    if (!nit || !archivos) return;
    escribirLocalStorage<PapeleraPersistida>(LS_KEY_PAPELERA, {
      nit,
      empresaNombre,
      archivos,
      recuperados: Array.from(recuperados),
    });
  }, [nit, empresaNombre, archivos, recuperados]);

  const ejecutar = () => {
    if (!nit) return;
    setIsLoading(true);
    setError(null);
    setArchivos(null);
    setRecuperados(new Set());
    sysadminService
      .listarPapeleraEmpresa(nit)
      .then((res) => {
        if (res.success && res.archivos) {
          setArchivos(res.archivos);
          setEmpresaNombre(res.empresaNombre ?? '');
        } else {
          setError(res.message || 'Error al listar la papelera');
        }
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Error al listar la papelera'))
      .finally(() => setIsLoading(false));
  };

  return (
    <div className="space-y-4">
      <div className="admin-card p-4 space-y-3">
        <p className="text-[12px] text-(--admin-text3) max-w-2xl">
          Lista lo que hoy está en la papelera de Drive dentro de la carpeta de una empresa — ya sea porque el borrado normal de la
          aplicación funcionó, o porque se eliminó desde esta misma auditoría. Cualquiera se puede recuperar mientras Google no lo haya
          purgado definitivamente (retención estándar de ~30 días).
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={nit}
            onChange={(e) => setNit(e.target.value)}
            disabled={cargandoEmpresas}
            className="text-[12px] py-1.5 px-2.5 max-w-xs border border-(--admin-border2) rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-(--admin-surface) text-(--admin-text)"
          >
            <option value="">Selecciona una empresa...</option>
            {empresas.map((e) => (
              <option key={e.nitId} value={e.nitId}>
                {e.nombre}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={ejecutar}
            disabled={!nit || isLoading}
            className="admin-btn text-[12px] py-1.5 px-3 disabled:opacity-50"
          >
            {isLoading ? 'Consultando...' : 'Ver papelera'}
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-(--admin-red) bg-(--admin-red-bg) px-4 py-3 text-[12px] text-(--admin-red)">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {isLoading && <LoadingContainer text="Consultando la papelera de Drive..." />}

      {archivos && !isLoading && (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[13px] font-semibold text-(--admin-text)">{empresaNombre}</span>
            <span className="admin-chip text-[11px] bg-(--admin-surface2) text-(--admin-text3)">
              {archivos.length} archivos en la papelera
            </span>
          </div>

          {archivos.length === 0 ? (
            <div className="admin-card p-10 text-center text-(--admin-text3)">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2 opacity-20" />
              <div className="text-[13px]">La papelera de Drive de esta empresa está vacía.</div>
            </div>
          ) : (
            <div className="admin-card overflow-x-auto">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Archivo</th>
                    <th>Ruta</th>
                    <th>Tipo</th>
                    <th>Estado</th>
                    <th>Acción</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {archivos.map((a) => {
                    const yaRecuperado = recuperados.has(a.id);
                    return (
                      <tr key={a.id}>
                        <td className="text-(--admin-text)">
                          <div className="flex items-center gap-1.5 max-w-60">
                            {a.mimeType.startsWith('audio/') ? (
                              <PlayCircle className="w-3.5 h-3.5 text-(--admin-text3) shrink-0" />
                            ) : (
                              <File className="w-3.5 h-3.5 text-(--admin-text3) shrink-0" />
                            )}
                            <span className="truncate whitespace-nowrap" title={a.name}>
                              {a.name}
                            </span>
                          </div>
                        </td>
                        <td className="text-(--admin-text3) text-[11px] font-mono max-w-70 truncate whitespace-nowrap" title={a.path}>
                          {a.path}
                        </td>
                        <td className="text-(--admin-text3) text-[11px] max-w-40 truncate whitespace-nowrap" title={a.mimeType}>
                          {a.mimeType}
                        </td>
                        <td>
                          {yaRecuperado ? (
                            <span className="admin-chip text-[10px] whitespace-nowrap bg-(--admin-green-bg) text-(--admin-green)">
                              Recuperado
                            </span>
                          ) : (
                            <span className="admin-chip text-[10px] whitespace-nowrap bg-(--admin-amber-bg) text-(--admin-amber)">
                              En la papelera
                            </span>
                          )}
                        </td>
                        <td>
                          {!yaRecuperado && (
                            <BotonEliminarRecuperar
                              fileId={a.id}
                              trashed={true}
                              onChanged={() => setRecuperados((prev) => new Set(prev).add(a.id))}
                            />
                          )}
                        </td>
                        <td>
                          {a.webViewLink && (
                            <a
                              href={a.webViewLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-(--admin-text3) hover:text-blue-600"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Página principal ──────────────────────────────────────────

export function AuditoriaDrivePage() {
  const [tab, setTab] = useState<'evidencias' | 'carpetas' | 'global' | 'papelera'>('evidencias');

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1.5">
        <h1 className="text-[18px] font-bold text-(--admin-text) tracking-tight flex items-center gap-2">
          <Trash2 className="w-5 h-5 text-(--admin-text3)" />
          Auditoría de Drive
        </h1>
        <p className="text-[12px] text-(--admin-text3)">
          Controla si las evidencias y audios "eliminados" realmente se eliminaron de Google Drive, y detecta archivos en las carpetas de
          las empresas que no coinciden con ningún registro activo.
        </p>
      </div>

      <div className="flex gap-1 border-b border-(--admin-border)">
        <button
          type="button"
          onClick={() => setTab('evidencias')}
          className={`px-3 py-2 text-[12px] font-medium border-b-2 transition-colors ${
            tab === 'evidencias' ? 'border-blue-600 text-blue-600' : 'border-transparent text-(--admin-text3) hover:text-(--admin-text)'
          }`}
        >
          Evidencias eliminadas
        </button>
        <button
          type="button"
          onClick={() => setTab('carpetas')}
          className={`px-3 py-2 text-[12px] font-medium border-b-2 transition-colors ${
            tab === 'carpetas' ? 'border-blue-600 text-blue-600' : 'border-transparent text-(--admin-text3) hover:text-(--admin-text)'
          }`}
        >
          Carpeta vs. BD activa
        </button>
        <button
          type="button"
          onClick={() => setTab('global')}
          className={`px-3 py-2 text-[12px] font-medium border-b-2 transition-colors ${
            tab === 'global' ? 'border-blue-600 text-blue-600' : 'border-transparent text-(--admin-text3) hover:text-(--admin-text)'
          }`}
        >
          Todas las empresas
        </button>
        <button
          type="button"
          onClick={() => setTab('papelera')}
          className={`px-3 py-2 text-[12px] font-medium border-b-2 transition-colors ${
            tab === 'papelera' ? 'border-blue-600 text-blue-600' : 'border-transparent text-(--admin-text3) hover:text-(--admin-text)'
          }`}
        >
          Papelera
        </button>
      </div>

      {tab === 'evidencias' && <EvidenciasEliminadasTab />}
      {tab === 'carpetas' && <CarpetaEmpresaTab />}
      {tab === 'global' && <AuditoriaGlobalTab />}
      {tab === 'papelera' && <PapeleraEmpresaTab />}
    </div>
  );
}
